import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AppConfigService } from '@app/app-config.service';
import { GIS_PORTAL_BASE, GIS_PORTAL_PATH } from '@app/modules/gis/gis-config';
import {
    hasValidGisToken,
    refreshGisToken,
    storeGisPortalConfig,
    startGisPkceFlow,
    getGisAuthRetryCount,
    clearGisAuthRetryCounter
} from '@app/modules/gis/gis-token';

/**
 * Guards all protected routes to ensure a valid GIS Portal (gis.xom.cloud)
 * access token exists in sessionStorage before any route loads.
 *
 * Must be placed AFTER MsalGuard in canActivate so Azure AD auth completes first.
 *
 * PKCE flow (first visit):
 *  1. No token → generate verifier/challenge → store verifier in sessionStorage
 *     → redirect to Portal /authorize.
 *  2. Portal redirects back to origin/?code=AUTH_CODE.
 *  3. main.ts intercepts ?code= before Angular boots, exchanges it for tokens
 *     via POST /token, stores them in sessionStorage, restores the return URL.
 *  4. Guard fires again → token found → returns true → route loads.
 *
 * Subsequent visits: valid token in sessionStorage → returns true immediately.
 * On expiry: silent refresh token exchange; falls back to full PKCE if refresh fails.
 */
@Injectable({ providedIn: 'root' })
export class GisAuthGuard implements CanActivate {

    constructor(private _appConfig: AppConfigService, private _router: Router) {}

    async canActivate(): Promise<boolean> {
        const appConfig = this._appConfig.getConfig();
        const config = appConfig && appConfig.arcgisConfig;

        if (!config) {
            console.error('GisAuthGuard: arcgisConfig missing from app-config.json');
            return true;
        }

        storeGisPortalConfig(GIS_PORTAL_BASE + GIS_PORTAL_PATH, config.appId);

        if (hasValidGisToken()) {
            clearGisAuthRetryCounter();
            return true;
        }

        if (await refreshGisToken()) {
            clearGisAuthRetryCounter();
            return true;
        }

        // Refresh failed — check if we've already attempted PKCE too many times
        const currentRetryCount = getGisAuthRetryCount();
        if (currentRetryCount >= 3) {
            console.error(`GisAuthGuard: PKCE auth failed after 3 attempts, redirecting to error page`);
            this._router.navigate(['/gis-auth-error']);
            return false;
        }

        await startGisPkceFlow();
        return false;
    }
}
